import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Megaphone, Building2, Users, Menu, X, LogOut,
  Import, CheckSquare, KanbanSquare, Flame, BarChart3, CalendarDays, FileText,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/leads", label: "Leads à traiter", icon: Flame },
  { to: "/prospects", label: "Prospects", icon: Import },
  { to: "/campagnes", label: "Campagnes", icon: Megaphone },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/calendrier", label: "Calendrier", icon: CalendarDays },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3 },
  { to: "/rapport-hebdo", label: "Rapport hebdo", icon: FileText },
  { to: "/entreprise", label: "Ma société", icon: Building2 },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users },
];

const UPCOMING = [
  { label: "Validation", icon: CheckSquare, phase: "Bientôt" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebar = (variant) => (
    <div className="flex h-full flex-col bg-primary text-primary-foreground">
      <div className="px-6 py-6">
        <p className="font-display text-xl font-semibold tracking-tight">RENFORTEO</p>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[hsl(39,63%,62%)]">Prospection</p>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${variant}-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                isActive ? "bg-white/10 font-semibold text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon size={17} /> {label}
          </NavLink>
        ))}
        <div className="pt-5">
          <p className="px-3 pb-2 text-[10px] uppercase tracking-widest text-white/40">À venir</p>
          {UPCOMING.map(({ label, icon: Icon, phase }) => (
            <div key={label} className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-white/35">
              <span className="flex items-center gap-3"><Icon size={17} /> {label}</span>
              <Badge variant="outline" className="border-white/20 text-[10px] text-white/40">{phase}</Badge>
            </div>
          ))}
        </div>
      </nav>
      <div className="border-t border-white/10 p-4">
        <p className="truncate text-sm font-medium" data-testid={`sidebar-${variant}-user-name`}>{user?.first_name} {user?.last_name}</p>
        <p className="truncate text-xs text-white/50">{user?.role_label}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          data-testid={`logout-${variant}-button`}
          className="mt-3 w-full justify-start gap-2 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <LogOut size={15} /> Se déconnecter
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 lg:block">{sidebar("desktop")}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64">{sidebar("mobile")}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/90 px-4 backdrop-blur lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
            {open ? <X size={20} /> : <Menu size={20} />}
          </Button>
          <span className="font-display font-semibold">RENFORTEO Prospection</span>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-8 lg:px-10 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
